import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import Accordion from 'react-bootstrap/Accordion'

import {
  modifyAllocation as modifyAllocationAction,
  deleteResource,
  deleteResources,
  getResourceAllocations,
  allocateSchedule,
  getTimesheetAuditLogs,
} from 'src/actions/timesheet.action'
import { getTaskdropdownData, getProjectList } from 'src/actions/project-management.action'
import { getUserNames, getUser } from 'src/actions/user-management.action'
import { successSnackBar, warningSnackBar, errorSnackBar } from 'src/actions/common.action'
import { filterLockedSections, convertToReactSelectFormat } from 'src/util/section-dropdown-format'
import { TIMESHEET_TASK_DROPDOWN_PARAMS } from '../task-dropdown-options'
import CustomInput from 'src/components/inputs/new-input'
import SelectInput from 'src/components/inputs/select'
import MultiSelect from 'src/components/inputs/multi-select'
import ConfirmationPopover from 'src/components/confirmation-popover'
import moment from 'moment'
import { formatDecimalTime } from 'src/util/common'
import { roundTimesheetHours } from 'src/util/decimal-utils'
import '../scheduling/scss/scheduling.scss'

/**
 * Format a Date or date string to "ddd, D MMM YYYY".
 *
 * @param {string|Date} dateVal - Input date string or object
 * @returns {string} Formatted human-readable date
 */
function formatDateLabel(dateVal) {
  if (!dateVal) return ''
  return moment.utc(dateVal).format('ddd, D MMM YYYY')
}

/**
 * Adds hours duration to an ISO date-time string while preserving timezone formatting.
 *
 * @param {string} baseTimeStr - ISO base time string
 * @param {number} hours - Number of hours to add
 * @returns {string|null} Resulting ISO date-time string or null if invalid
 */
function addHoursToTime(baseTimeStr, hours) {
  const m = moment.parseZone(baseTimeStr)
  if (!m.isValid()) return null
  const durationMs = Math.round(hours * 3600000)
  const result = m.clone().add(durationMs, 'milliseconds')
  return baseTimeStr.endsWith('Z') ? result.toISOString() : result.format()
}

/**
 * Common Modern Edit Timesheet Dialog.
 *
 * Serves as the unified multi-task entry breakdown editor across workforce
 * scheduling (clicking an assigned day in the grid) and Timesheet Approval
 * ("Modify" or "View" action in AllocationsView).
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Modal visibility flag
 * @param {Function} props.onClose - Modal close handler
 * @param {Object} [props.staff] - Target staff member object ({ id, name, role, initials })
 * @param {string} [props.date] - Date string in YYYY-MM-DD format
 * @param {Array} [props.initialEntries] - Pre-loaded timesheet allocation records
 * @param {string} [props.allocationId] - Timesheet allocation UUID when opened from approval table
 * @param {Array} [props.projects] - List of available project objects
 * @param {boolean} [props.isViewMode] - View-only mode flag
 * @param {Function} [props.onSuccess] - Callback invoked on successful save/deletion
 * @returns {React.ReactElement} Rendered dialog
 */
export default function EditTimesheetDialog({
  open,
  onClose,
  staff,
  date,
  projectId,
  initialEntries,
  allocationId,
  projects: initialProjects,
  isViewMode = false,
  onSuccess,
}) {
  const dispatch = useDispatch()
  const currentUser = useSelector((state) => state.user?.userData)

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteShiftAnchor, setDeleteShiftAnchor] = useState(null)
  const [deleteRowTarget, setDeleteRowTarget] = useState(null)
  const [projects, setProjects] = useState(initialProjects || [])
  const [resolvedStaff, setResolvedStaff] = useState(staff || null)
  const [resolvedDate, setResolvedDate] = useState(date || '')
  const [activeAllocationId, setActiveAllocationId] = useState(allocationId || null)
  const [initialAllocationIds, setInitialAllocationIds] = useState([])
  const [dayShiftStartTime, setDayShiftStartTime] = useState('')
  const [items, setItems] = useState([])
  const [auditLogs, setAuditLogs] = useState([])
  const [tracking, setTracking] = useState({ createdAt: null, createdBy: null, updatedAt: null, updatedBy: null })
  const [userNameMap, setUserNameMap] = useState({})

  const projectTasksMapRef = useRef({})
  const [loadingTasksMap, setLoadingTasksMap] = useState({})

  /**
   * Loads task dropdown options for a given project ID and caches in projectTasksMapRef.
   *
   * @param {string} projId - Project UUID
   * @returns {Promise<Array>} Resolved task options
   */
  const loadTasksForProject = useCallback(
    async (projId) => {
      if (!projId) return []
      if (projectTasksMapRef.current[projId]) {
        return projectTasksMapRef.current[projId]
      }
      setLoadingTasksMap((prev) => ({ ...prev, [projId]: true }))
      try {
        const response = await dispatch(getTaskdropdownData(projId, TIMESHEET_TASK_DROPDOWN_PARAMS))
        const filtered = filterLockedSections(response || [], [])
        const formatted = convertToReactSelectFormat(filtered, null, true)
        projectTasksMapRef.current[projId] = formatted
        return formatted
      } catch {
        projectTasksMapRef.current[projId] = []
        return []
      } finally {
        setLoadingTasksMap((prev) => ({ ...prev, [projId]: false }))
      }
    },
    [dispatch]
  )

  /**
   * Initializes or fetches allocations and project dropdown options when dialog opens.
   */
  useEffect(() => {
    if (!open) {
      setDeleteShiftAnchor(null)
      setDeleteRowTarget(null)
      setInitialAllocationIds([])
      setDayShiftStartTime('')
      setAuditLogs([])
      setTracking({ createdAt: null, createdBy: null, updatedAt: null, updatedBy: null })
      setUserNameMap({})
      return
    }

    let isMounted = true

    async function initialize() {
      setLoading(true)
      try {
        // Ensure project list is available
        const sourceProjects = (projects && projects.length > 0) ? projects : initialProjects
        let projList = Array.isArray(sourceProjects) && sourceProjects.length > 0
          ? sourceProjects
            .filter((p) => p.status === 'IN_PROGRESS' || !p.status)
            .map((p) => ({
              id: p.id || p.value,
              name: p.name || p.key || p.description,
              type: p.projectType || p.type || 'PROJECT',
            }))
          : []

        if (projList.length === 0) {
          const fetchedProjects = await dispatch(getProjectList(null, 'PROJECT', null)).catch(() => [])
          projList = Array.isArray(fetchedProjects)
            ? fetchedProjects
              .filter((p) => p.status === 'IN_PROGRESS' || !p.status)
              .map((p) => ({
                id: p.id || p.value,
                name: p.name || p.key,
                type: p.projectType || p.type || 'PROJECT',
              }))
            : []
        }
        if (isMounted) setProjects(projList)

        let rawEntries = initialEntries || []
        let primaryAllocId = allocationId || null
        let resolvedUser = staff || null
        let entryDate = date || ''
        let targetProjectId = projectId || null

        // If no initial entries were provided, load allocations for the day and project
        if (!rawEntries || rawEntries.length === 0) {
          let targetUserId = resolvedUser?.id || null
          let targetDate = entryDate || null

          // If either user ID, date, or targetProjectId is missing, fetch the single allocation by ID first
          if ((!targetUserId || !targetDate || !targetProjectId) && allocationId) {
            const singleResult = await dispatch(getResourceAllocations({ allocationId })).catch(() => null)
            const singleRecords = singleResult?.data ?? (Array.isArray(singleResult) ? singleResult : [])
            if (singleRecords.length > 0) {
              const single = singleRecords[0]
              primaryAllocId = single.id
              targetUserId = targetUserId || single.userId
              targetDate = targetDate || (typeof single.date === 'string' && single.date.length >= 10
                ? single.date.slice(0, 10)
                : null) || single.startTime?.slice(0, 10) || ''
              targetProjectId = targetProjectId || single.projectId || single.project?.id || null

              let resourceName = resolvedUser?.name || single.empName || ''
              if (!resourceName && single.userId) {
                const allUsers = await dispatch(getUserNames()).catch(() => [])
                const matchedUser = Array.isArray(allUsers) && allUsers.find((u) => u.id === single.userId)
                if (matchedUser) {
                  resourceName = `${matchedUser.firstName || ''} ${matchedUser.lastName || ''}`.trim()
                }
              }

              resolvedUser = {
                id: single.userId,
                name: resourceName || 'Resource',
                role: resolvedUser?.role || '',
                initials: (resourceName || 'R').slice(0, 2).toUpperCase(),
              }
              rawEntries = singleRecords
            }
          }

          // Fetch all same-day allocations for the user and project in a single batch
          if (targetUserId && targetDate) {
            entryDate = targetDate
            if (!resolvedUser?.name) {
              const allUsers = await dispatch(getUserNames()).catch(() => [])
              const matchedUser = Array.isArray(allUsers) && allUsers.find((u) => u.id === targetUserId)
              if (matchedUser) {
                const name = `${matchedUser.firstName || ''} ${matchedUser.lastName || ''}`.trim()
                resolvedUser = {
                  id: targetUserId,
                  name: name || 'Resource',
                  role: resolvedUser?.role || '',
                  initials: (name || 'R').slice(0, 2).toUpperCase(),
                }
              }
            }

            const dayResults = await dispatch(
              getResourceAllocations({
                userId: targetUserId,
                startTime: `${targetDate}T00:00:00.000Z`,
                endTime: `${targetDate}T23:59:59.999Z`,
                ...(targetProjectId ? { projectId: targetProjectId } : {}),
              })
            ).catch(() => null)
            const dayRecords = dayResults?.data ?? (Array.isArray(dayResults) ? dayResults : [])
            const filteredRecords = dayRecords.filter((r) => {
              const matchesProject = !targetProjectId || (r.projectId || r.project?.id) === targetProjectId
              const rawDate = r.startTime || r.start_time || ''
              const entryDateIso = typeof r.date === 'string' && r.date.length >= 10
                ? r.date.slice(0, 10)
                : rawDate.slice(0, 10)
              const entryDateZone = rawDate ? moment.parseZone(rawDate).format('YYYY-MM-DD') : ''
              const matchesDate = !targetDate || entryDateIso === targetDate || entryDateZone === targetDate
              return matchesProject && matchesDate
            })
            if (filteredRecords.length > 0) {
              rawEntries = filteredRecords
            }
          }
        }

        if (!isMounted) return

        setActiveAllocationId(primaryAllocId)
        setResolvedStaff(resolvedUser)
        setResolvedDate(entryDate)

        const existingIds = (rawEntries || [])
          .map((e) => e.id)
          .filter((id) => Boolean(id) && typeof id === 'string' && !id.startsWith('entry-'))
        const allInitialIds = [...new Set([...existingIds, primaryAllocId].filter(Boolean))]
        setInitialAllocationIds(allInitialIds)

        // Creator/modifier tracking: earliest createdAt + its created_by, latest updated_date + its updated_by
        const tracked = (rawEntries || []).filter((e) => e.createdAt || e.created_by || e.updated_date || e.updated_by)
        const earliestCreated = tracked.filter((e) => e.createdAt)
          .reduce((acc, e) => (new Date(e.createdAt) < new Date(acc.createdAt) ? e : acc), tracked.find((e) => e.createdAt) || null)
        const latestUpdated = tracked.filter((e) => e.updated_date)
          .reduce((acc, e) => (new Date(e.updated_date) > new Date(acc.updated_date) ? e : acc), tracked.find((e) => e.updated_date) || null)
        const trackingMeta = {
          createdAt: earliestCreated?.createdAt || null,
          createdBy: earliestCreated?.created_by || null,
          updatedAt: latestUpdated?.updated_date || null,
          updatedBy: latestUpdated?.updated_by || null,
        }
        setTracking(trackingMeta)

        // Resolve creator/modifier display names
        const trackedUserIds = [...new Set([trackingMeta.createdBy, trackingMeta.updatedBy].filter(Boolean))]
        if (trackedUserIds.length > 0) {
          const nameMap = {}
          const allUsers = await Promise.resolve(dispatch(getUserNames())).catch(() => [])
          if (Array.isArray(allUsers)) {
            allUsers.forEach((u) => {
              const name = `${u.firstName || ''} ${u.lastName || ''}`.trim()
              if (u.id && name) nameMap[u.id] = name
            })
          }
          // The actor may not appear in the company user list (e.g. a global
          // admin); fall back to the logged-in user's profile, then remote fetch.
          trackedUserIds.forEach((id) => {
            if (!nameMap[id] && currentUser && currentUser.id === id) {
              const name = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim()
              nameMap[id] = name || currentUser.fullName || currentUser.email || ''
            }
          })
          await Promise.all(trackedUserIds.filter((id) => !nameMap[id]).map(async (id) => {
            const res = await Promise.resolve(dispatch(getUser(id))).catch(() => null)
            const payload = res && (res.data || res)
            if (payload) {
              const name = `${payload.firstName || ''} ${payload.lastName || ''}`.trim()
              const resolved = name || payload.fullName || payload.email || ''
              if (resolved) nameMap[id] = resolved
            }
          }))
          if (isMounted) setUserNameMap(nameMap)
        }

        // Audit logs across every allocation shown in the dialog
        if (allInitialIds.length > 0) {
          const auditResults = await Promise.all(
            allInitialIds.map((id) => Promise.resolve(dispatch(getTimesheetAuditLogs(id))).catch(() => null))
          )
          const mergedLogs = auditResults
            .flatMap((res) => res?.data ?? (Array.isArray(res) ? res : []))
            .filter((log, i, arr) => log && arr.findIndex((l) => l.id === log.id) === i)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          if (isMounted) setAuditLogs(mergedLogs)
        }

        // Map entries to breakdown items in chronological order (earliest startTime first)
        if (rawEntries && rawEntries.length > 0) {
          const sortedEntries = [...rawEntries].sort((a, b) => {
            const timeA = new Date(a.startTime || a.start_time || 0).getTime()
            const timeB = new Date(b.startTime || b.start_time || 0).getTime()
            if (timeA !== timeB) return timeA - timeB
            const createdA = new Date(a.createdAt || a.created_at || 0).getTime()
            const createdB = new Date(b.createdAt || b.created_at || 0).getTime()
            if (createdA !== createdB) return createdA - createdB
            return (a.id || '').localeCompare(b.id || '')
          })

          const initialShiftStart = sortedEntries[0]?.startTime || `${entryDate}T08:00:00.000Z`
          setDayShiftStartTime(initialShiftStart)

          const mappedItems = sortedEntries.map((e, index) => {
            const rawHours = e.startTime && e.endTime
              ? Math.abs(new Date(e.endTime) - new Date(e.startTime)) / 3600000
              : (e.entryHours ? Number(e.entryHours) : 8.0)
            const entryHours = String(roundTimesheetHours(rawHours))

            const itemProjId = e.projectId || e.project?.id || (projList[0]?.id ?? '')
            return {
              id: e.id || `entry-${Date.now()}-${index}`,
              allocationId: e.id || null,
              projectId: itemProjId,
              projectType: e.projectType || 'PROJECT',
              selectedTask: (e.taskId || e.stageId) ? {
                value: e.taskId || e.stageId,
                label: e.taskName || e.f_taskName || 'Selected Task',
                displayName: e.taskName || e.f_taskName || 'Selected Task',
                isStage: Boolean(e.stageId && !e.taskId),
                taskType: e.taskType || (e.taskId ? 'TASK' : ''),
              } : null,
              hours: String(entryHours),
              startTime: e.startTime,
              endTime: e.endTime,
              originalStartTime: e.startTime || null,
              originalEndTime: e.endTime || null,
            }
          })
          setItems(mappedItems)

          // Preload tasks for each distinct project in the entries
          const distinctProjectIds = [...new Set(mappedItems.map((m) => m.projectId).filter(Boolean))]
          await Promise.all(distinctProjectIds.map((pId) => loadTasksForProject(pId)))
        } else {
          // Brand new entry on an empty day
          const defaultProjId = projList[0]?.id || ''
          const fallbackItem = {
            id: `entry-${Date.now()}-0`,
            allocationId: null,
            projectId: defaultProjId,
            projectType: projList[0]?.type || 'PROJECT',
            selectedTask: null,
            hours: '8.0',
            startTime: null,
            endTime: null,
            originalStartTime: null,
            originalEndTime: null,
          }
          setItems([fallbackItem])
          if (defaultProjId) {
            await loadTasksForProject(defaultProjId)
          }
        }
      } catch (err) {
        console.error('[EditTimesheetDialog] initialize error:', err)
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    initialize()

    return () => {
      isMounted = false
    }
  }, [open, allocationId, initialEntries, staff, date, initialProjects, dispatch, loadTasksForProject])

  const totalDailyHours = useMemo(() => {
    return items.reduce((acc, it) => {
      const parsed = parseFloat(it.hours)
      return acc + (Number.isFinite(parsed) && parsed > 0 ? parsed : 0)
    }, 0)
  }, [items])

  const roundedDailyHours = roundTimesheetHours(totalDailyHours)

  /**
   * Dynamically computes start and end times for all allocation items live.
   * Preserves independent database start times for existing non-contiguous entries,
   * cascades start times for contiguous entries when preceding duration changes,
   * and chains new rows sequentially after preceding rows.
   */
  const computedRowTimes = useMemo(() => {
    const defaultStart = dayShiftStartTime || (resolvedDate ? `${resolvedDate}T08:00:00.000Z` : new Date().toISOString())
    const results = []

    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      const prevItem = i > 0 ? items[i - 1] : null
      const prevResult = i > 0 ? results[i - 1] : null

      const h = parseFloat(it.hours)
      const validHours = Number.isFinite(h) && h > 0 ? h : 0

      const origStart = it.originalStartTime || it.startTime || null
      const hasOriginalStart = Boolean(origStart && moment(origStart).isValid())

      const prevOrigEnd = prevItem ? (prevItem.originalEndTime || prevItem.endTime || null) : null
      const isContiguousWithPrev = Boolean(
        hasOriginalStart &&
        prevOrigEnd &&
        moment(prevOrigEnd).isValid() &&
        Math.abs(new Date(origStart).getTime() - new Date(prevOrigEnd).getTime()) <= 60000
      )

      let rowStart
      if (!hasOriginalStart) {
        // Newly added row or row without original start time
        rowStart = prevResult?.endTime || defaultStart
      } else if (isContiguousWithPrev && prevResult?.endTime) {
        // Cascades from previous row's end time if they were originally contiguous
        rowStart = prevResult.endTime
      } else {
        // Independent entry: preserve original start time
        rowStart = origStart
      }

      const rowEnd = addHoursToTime(rowStart, validHours)

      results.push({
        startTime: rowStart,
        endTime: rowEnd,
      })
    }

    return results
  }, [items, dayShiftStartTime, resolvedDate])

  /**
   * Adds an additional task allocation row to the breakdown.
   */
  const handleAddRow = () => {
    if (isViewMode || roundedDailyHours >= 24) return
    const remaining = Math.max(0, roundTimesheetHours(24 - roundedDailyHours))
    const initialHours = remaining > 0 ? String(roundTimesheetHours(Math.min(2.0, remaining))) : '2.0'
    const defaultProj = projects.find((p) => p.id === items[0]?.projectId) || projects[0]

    const newItem = {
      id: `entry-${Date.now()}-${items.length}`,
      allocationId: null,
      projectId: defaultProj?.id || '',
      projectType: defaultProj?.type || 'PROJECT',
      selectedTask: null,
      hours: initialHours,
      startTime: null,
      endTime: null,
      originalStartTime: null,
      originalEndTime: null,
    }

    setItems((prev) => [...prev, newItem])
    if (newItem.projectId) {
      loadTasksForProject(newItem.projectId)
    }
  }

  /**
   * Removes a task allocation row by its local ID.
   *
   * @param {string} id - Row ID
   */
  const handleRemoveRow = (id) => {
    if (isViewMode || items.length <= 1) return
    setItems((prev) => prev.filter((it) => it.id !== id))
  }

  /**
   * Updates project selection on a breakdown row.
   *
   * @param {string} id - Row ID
   * @param {string} newProjectId - Selected project UUID
   */
  const handleProjectChange = (id, newProjectId) => {
    const proj = projects.find((p) => p.id === newProjectId)
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? {
            ...it,
            projectId: newProjectId,
            projectType: proj?.type || 'PROJECT',
            selectedTask: null,
          }
          : it
      )
    )
    if (newProjectId) {
      loadTasksForProject(newProjectId)
    }
  }

  /**
   * Updates task selection on a breakdown row.
   *
   * @param {string} id - Row ID
   * @param {Object} taskOption - Selected task option object
   */
  const handleTaskChange = (id, taskOption) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, selectedTask: taskOption } : it))
    )
  }

  /**
   * Updates duration hours on a breakdown row.
   *
   * @param {string} id - Row ID
   * @param {string} val - Sanitized input string
   */
  const handleHoursChange = (id, val) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, hours: val } : it))
    )
  }

  /**
   * Saves changes using modifyAllocation or allocateSchedule.
   */
  const handleSave = async () => {
    if (isViewMode || items.length === 0) return

    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      const rowNum = items.length > 1 ? ` #${i + 1}` : ''
      if (!it.projectId) {
        dispatch(warningSnackBar(`Please select a project for entry${rowNum}.`))
        return
      }
      const h = parseFloat(it.hours)
      if (!Number.isFinite(h) || h <= 0) {
        dispatch(warningSnackBar(`Please enter valid hours for entry${rowNum}.`))
        return
      }
      it.hours = String(roundTimesheetHours(it.hours))
    }

    if (roundedDailyHours > 24) {
      dispatch(warningSnackBar(`Total daily hours cannot exceed 24.0h (currently ${roundedDailyHours}h).`))
      return
    }

    const userId = resolvedStaff?.id
    if (!userId) {
      dispatch(errorSnackBar('Staff user ID is missing.'))
      return
    }

    setSubmitting(true)
    try {
      const allocationsPayload = items.map((it, idx) => {
        const rowTime = computedRowTimes[idx] || {}
        return {
          startTime: rowTime.startTime,
          endTime: rowTime.endTime,
          projectId: it.projectId,
          projectType: it.projectType || 'PROJECT',
          taskId: it.selectedTask && !it.selectedTask.isStage ? (it.selectedTask.value || null) : null,
          stageId: it.selectedTask?.isStage ? (it.selectedTask.value || null) : null,
          taskType: it.selectedTask?.taskType || (it.selectedTask && !it.selectedTask.isStage ? 'TASK' : null),
          taskName: it.selectedTask?.displayName || it.selectedTask?.label || null,
          allDayWithBreaks: true,
        }
      })

      const allExistingIds = [
        ...new Set([
          ...initialAllocationIds,
          activeAllocationId,
          ...items.map((it) => it.allocationId),
        ].filter(Boolean))
      ]

      const targetAllocationId = allExistingIds.length > 1
        ? allExistingIds
        : (allExistingIds[0] || activeAllocationId || null)

      if (targetAllocationId) {
        const body = {
          allocationId: targetAllocationId,
          allocationType: 'WORK',
          userId,
          projectId: items[0].projectId,
          projectType: items[0].projectType || 'PROJECT',
          allocations: allocationsPayload,
        }
        await dispatch(modifyAllocationAction(body))
      } else {
        // Direct allocation creation
        await dispatch(
          allocateSchedule({
            users: [
              {
                id: userId,
                leaves: [],
                allocations: allocationsPayload.map((a) => ({
                  ...a,
                  id: '',
                  hourlyRate: resolvedStaff?.hourlyRate || 0,
                  previousType: '',
                })),
              },
            ],
          })
        )
      }

      if (onSuccess) onSuccess()
      onClose()
    } catch (e) {
      dispatch(warningSnackBar(e?.message || 'Failed to update timesheet allocations.'))
      console.error('[EditTimesheetDialog] handleSave error:', e)
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Deletes the entire shift / daily allocation (all task entries for the day).
   */
  const handleDeleteShift = async () => {
    if (isViewMode) return
    const allAllocationIds = [
      ...new Set([
        ...initialAllocationIds,
        activeAllocationId,
        ...items.map((it) => it.allocationId),
      ].filter(Boolean))
    ]
    if (allAllocationIds.length === 0) {
      onClose()
      return
    }

    setDeleting(true)
    try {
      if (allAllocationIds.length > 1) {
        await dispatch(deleteResources('WORK', { allocationIds: allAllocationIds }))
      } else {
        await dispatch(deleteResource('WORK', allAllocationIds[0]))
      }
      dispatch(successSnackBar('Shift deleted successfully'))
      if (onSuccess) onSuccess()
      onClose()
    } catch (e) {
      dispatch(errorSnackBar(e?.message || 'Failed to delete shift.'))
      console.error('[EditTimesheetDialog] handleDeleteShift error:', e)
    } finally {
      setDeleting(false)
    }
  }

  const canSave = !isViewMode &&
    !loading &&
    !submitting &&
    !deleting &&
    items.length > 0 &&
    items.every((it) => !!it.projectId && parseFloat(it.hours) > 0) &&
    roundedDailyHours <= 24

  const displayUserName = (userId) => {
    if (!userId) return ''
    return userNameMap[userId] || 'Inactive User'
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="scheduling-dialog"
      disableScrollLock
    >
      <DialogTitle>
        <Grid container alignItems="center">
          <Grid item xs>
            <div className="d-flex align-items-center gap-2">
              <span className="dialog-header-title">
                {isViewMode ? 'View Timesheet' : 'Edit Timesheet'}
              </span>
              <Chip
                size="small"
                color={roundedDailyHours > 24 ? 'error' : 'primary'}
                label={`${roundedDailyHours}h (${formatDecimalTime(roundedDailyHours)}) total`}
                sx={{ fontWeight: 600 }}
              />
            </div>
            <div className="dialog-subtitle">
              {resolvedStaff?.name || 'Resource'} · {formatDateLabel(resolvedDate)}
            </div>
          </Grid>
          <Grid item>
            <IconButton onClick={onClose} aria-label="Close">
              <CloseIcon sx={{ color: '#d0021b' }} />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <div className="d-flex flex-column align-items-center justify-content-center gap-2 py-5">
            <CircularProgress size={36} />
            <span className="text-secondary">Loading allocations...</span>
          </div>
        ) : (
          <>

            {roundedDailyHours > 24 && (
              <div className="alert alert-danger d-flex align-items-center gap-2 mb-3 py-2" role="alert">
                <WarningAmberIcon sx={{ fontSize: 20 }} />
                <span>
                  Total daily hours cannot exceed 24.0h across all entries (currently {roundedDailyHours}h).
                </span>
              </div>
            )}

            <div className="allocation-table-container">
              <div className="allocation-table-header">
                <div className="col-badge">#</div>
                <div className="col-project">
                  Project <span className="text-danger">*</span>
                </div>
                <div className="col-task">Task (optional)</div>
                <div className="col-time">Date & Time</div>
                <div className="col-hours">Hours</div>
                <div className="col-duration">Duration</div>
                {!isViewMode && <div className="col-action" />}
              </div>

              <div className="allocation-table-body">
                {items.map((item, index) => {
                  const tasks = projectTasksMapRef.current[item.projectId] || []
                  const isLoadingTasks = Boolean(loadingTasksMap[item.projectId])
                  const rawH = item.hours
                  const parsedH = parseFloat(rawH)
                  const isHoursInvalid = rawH === '' || !Number.isFinite(parsedH) || parsedH <= 0 || parsedH > 24
                  const rowTime = computedRowTimes[index] || {}
                  const durationFormatted = isHoursInvalid ? '0H' : formatDecimalTime(item.hours)

                  return (
                    <div key={item.id} className="allocation-table-row">
                      <div className="col-badge">
                        <span className="task-row-badge">Task #{index + 1}</span>
                      </div>

                      <div className="col-project">
                        <SelectInput
                          label=""
                          required={true}
                          placeholder="Select Project"
                          value={item.projectId}
                          onChange={(e) => handleProjectChange(item.id, e.target.value)}
                          items={projects.map((p) => ({ value: p.id, label: p.name }))}
                          disabled={isViewMode}
                        />
                      </div>

                      <div className="col-task">
                        <MultiSelect
                          label=""
                          placeholder={isLoadingTasks ? 'Loading tasks...' : 'Select Task'}
                          items={tasks}
                          value={item.selectedTask}
                          onChangeHandler={(opt) => handleTaskChange(item.id, opt)}
                          isStage={true}
                          disabled={isViewMode || !item.projectId || isLoadingTasks}
                          isClearable={!isViewMode}
                        />
                      </div>

                      <div className="col-time">
                        <div className="task-row-time-badge">
                          {rowTime.startTime && moment(rowTime.startTime).isValid() ? (
                            <div className="time-badge-inner">
                              <span className="time-badge-date">
                                {moment.parseZone(rowTime.startTime).format('DD-MM-YYYY')}
                              </span>
                              <span className="time-badge-range">
                                {`${moment.parseZone(rowTime.startTime).format('HH:mm')} - ${rowTime.endTime && moment(rowTime.endTime).isValid() ? moment.parseZone(rowTime.endTime).format('HH:mm') : '—'}`}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </div>
                      </div>

                      <div className="col-hours">
                        <CustomInput
                          label=""
                          name={`hours-${item.id}`}
                          type="number"
                          value={item.hours}
                          onChange={(e) => handleHoursChange(item.id, e.target.value)}
                          step="any"
                          min={0.1}
                          max={24}
                          disabled={isViewMode}
                          customInputClass={isHoursInvalid ? 'hours-input-invalid' : ''}
                        />
                      </div>

                      <div className="col-duration">
                        <span className="task-row-duration-pill">
                          {durationFormatted}
                        </span>
                      </div>

                      {!isViewMode && (
                        <div className="col-action">
                          {items.length > 1 ? (
                            <IconButton
                              size="small"
                              onClick={(e) => setDeleteRowTarget({ anchorEl: e.currentTarget, rowId: item.id })}
                              aria-label={`Remove Task ${index + 1}`}
                              title="Remove task entry"
                              className="row-delete-icon-btn"
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          ) : (
                            <div className="action-placeholder" />
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {!isViewMode && (
              <div className="allocation-table-footer">
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddRow}
                  disabled={roundedDailyHours >= 24}
                  className="add-allocation-btn"
                >
                  Add Task
                </Button>
                <div className="total-hours-wrapper">
                  <span>Total Daily Hours:</span>
                  <span className={`total-hours-pill ${roundedDailyHours > 24 ? 'exceeded' : ''}`}>
                    {roundedDailyHours}h ({formatDecimalTime(roundedDailyHours)})
                    {roundedDailyHours > 24 && ' (Exceeds 24h limit)'}
                  </span>
                </div>
              </div>
            )}

            {(tracking.createdAt || tracking.createdBy || tracking.updatedAt || tracking.updatedBy) && (
              <div className="d-flex flex-wrap gap-2 mt-3" data-cy="allocation-tracking">
                {(tracking.createdAt || tracking.createdBy) && (
                  <span className="badge bg-light text-dark border fw-normal" data-cy="allocation-created-meta">
                    {tracking.createdAt && `Created ${moment.utc(tracking.createdAt).local().format('MMM D, YYYY HH:mm')}`}
                    {tracking.createdBy && ` by ${displayUserName(tracking.createdBy)}`}
                  </span>
                )}
                {(tracking.updatedAt || tracking.updatedBy) && (
                  <span className="badge bg-light text-dark border fw-normal" data-cy="allocation-updated-meta">
                    {tracking.updatedAt && `Last modified ${moment.utc(tracking.updatedAt).local().format('MMM D, YYYY HH:mm')}`}
                    {tracking.updatedBy && ` by ${displayUserName(tracking.updatedBy)}`}
                  </span>
                )}
              </div>
            )}

            {initialAllocationIds.length > 0 && (
              <div className="timesheet-audit-logs mt-3 mb-3 ">
                <Accordion data-cy="audit-logs-accordion">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header data-cy="audit-logs-header">Audit Logs ({auditLogs.length})</Accordion.Header>
                    <Accordion.Body>
                      <div className="audit-log-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <table className="table table-sm table-hover">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Reviewer</th>
                              <th>Action</th>
                              <th>Task</th>
                            </tr>
                          </thead>
                          <tbody>
                            {auditLogs.map((log) => (
                              <tr key={log.id}>
                                <td className="text-nowrap">{moment.utc(log.created_at).local().format('MMM D, YYYY HH:mm')}</td>
                                <td>{log.reviewer_name}</td>
                                <td>
                                  <span className={`badge ${log.action === 'APPROVED' ? 'bg-success' : log.action === 'REJECTED' ? 'bg-danger' : 'bg-warning'}`}>
                                    {log.action}
                                  </span>
                                </td>
                                <td>{log.task_name || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </div>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions className="edit-timesheet-dialog-actions" sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center' }}>
        <div className="dialog-footer-left">
          {!isViewMode && (
            <div className="dialog-footer-hint">
              <InfoOutlinedIcon />
              <span>Click <strong>Save Changes</strong> to apply all additions, edits, or removals.</span>
            </div>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          {!isViewMode && (activeAllocationId || items.some((it) => it.allocationId)) && (
            <Button
              variant="outlined"
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => setDeleteShiftAnchor(e.currentTarget)}
              disabled={deleting || submitting}
              className="delete-shift-btn"
            >
              {deleting ? 'Deleting...' : 'Delete Shift'}
            </Button>
          )}
          <button className="cancel-btn-white" onClick={onClose} type="button">
            {isViewMode ? 'Close' : 'Cancel'}
          </button>
          {!isViewMode && (
            <button
              className={`btnPrimary ${!canSave ? 'save-btn-disabled' : ''}`}
              disabled={!canSave}
              onClick={handleSave}
              type="button"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </DialogActions>

      {/* Popover confirmation for Delete Shift */}
      <ConfirmationPopover
        open={Boolean(deleteShiftAnchor)}
        anchorEl={deleteShiftAnchor}
        onClose={() => setDeleteShiftAnchor(null)}
        onConfirm={() => {
          setDeleteShiftAnchor(null)
          handleDeleteShift()
        }}
        id="popover-delete-shift"
      />

      {/* Popover confirmation for Task Row Deletion */}
      <ConfirmationPopover
        open={Boolean(deleteRowTarget)}
        anchorEl={deleteRowTarget?.anchorEl}
        onClose={() => setDeleteRowTarget(null)}
        onConfirm={() => {
          if (deleteRowTarget?.rowId) {
            handleRemoveRow(deleteRowTarget.rowId)
          }
          setDeleteRowTarget(null)
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        arrowPosition="right"
        id="popover-delete-task"
      />
    </Dialog>
  )
}

EditTimesheetDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  staff: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    role: PropTypes.string,
    initials: PropTypes.string,
    hourlyRate: PropTypes.number,
  }),
  date: PropTypes.string,
  projectId: PropTypes.string,
  initialEntries: PropTypes.array,
  allocationId: PropTypes.string,
  projects: PropTypes.array,
  isViewMode: PropTypes.bool,
  onSuccess: PropTypes.func,
}
